import { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Loader2, Package } from "lucide-react";

const inputCls = "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";

export default function ProductCodes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", description: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/product-codes"); setRows(data); } catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (form.code.length !== 1 || !form.name.trim()) { toast.error("A single-character code and name are required"); return; }
    setBusy(true);
    try { await api.post("/admin/product-codes", form); toast.success("Product code created"); setCreateOpen(false); setForm({ code: "", name: "", description: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try { await api.put(`/admin/product-codes/${editTarget.id}`, { name: editTarget.name, description: editTarget.description }); toast.success("Updated"); setEditTarget(null); load(); }
    catch (e) { toast.error("Failed"); } finally { setBusy(false); }
  };

  const toggleActive = async (p) => {
    try { await api.put(`/admin/product-codes/${p.id}`, { active: !p.active }); toast.success(p.active ? "Deactivated" : "Activated"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <DashboardLayout title="Product Directory">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Catalog</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Product code directory</h2>
          <p className="mt-1 text-sm text-neutral-500">One-character product codes used in secure bottle codes.</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A]" data-testid="create-product-btn"><Plus className="h-4 w-4" /> New Product Code</button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#141414]" data-testid="products-table">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center"><span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/[0.04] text-neutral-500"><Package className="h-6 w-6" /></span><p className="mt-4 text-sm text-neutral-400">No product codes yet</p></div>
        ) : (
          <table className="w-full text-left">
            <thead><tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500"><th className="px-6 py-4 font-medium">Code</th><th className="px-6 py-4 font-medium">Name</th><th className="px-6 py-4 font-medium">Description</th><th className="px-6 py-4 font-medium">Status</th><th className="px-6 py-4 text-right font-medium">Actions</th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-white/[0.04] hover:bg-white/[0.03]" data-testid={`product-row-${p.code}`}>
                  <td className="px-6 py-4 font-mono text-sm text-white">{p.code}</td>
                  <td className="px-6 py-4 text-sm text-white">{p.name}</td>
                  <td className="px-6 py-4 text-sm text-neutral-400">{p.description || "—"}</td>
                  <td className="px-6 py-4"><span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${p.active ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-white/5 text-neutral-400 border-white/10"}`}>{p.active ? "Active" : "Inactive"}</span></td>
                  <td className="px-6 py-4"><div className="flex justify-end gap-2">
                    <button onClick={() => setEditTarget({ ...p })} className="rounded-lg border border-white/[0.1] px-3 py-1.5 text-xs text-neutral-200 hover:bg-white/[0.05]" data-testid={`edit-product-${p.code}`}>Edit</button>
                    <button onClick={() => toggleActive(p)} className="rounded-lg border border-white/[0.1] px-3 py-1.5 text-xs text-neutral-300 hover:bg-white/[0.05]" data-testid={`toggle-product-${p.code}`}>{p.active ? "Deactivate" : "Activate"}</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="create-product-modal">
          <DialogHeader><DialogTitle>New Product Code</DialogTitle><DialogDescription className="text-neutral-500">Codes must be a single character (e.g. A) and unique.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-neutral-300">Code (1 character)</Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 1).toUpperCase() }))} placeholder="A" className={inputCls} data-testid="product-code-input" /></div>
            <div className="space-y-2"><Label className="text-neutral-300">Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="product-name-input" /></div>
            <div className="space-y-2"><Label className="text-neutral-300">Description (optional)</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="product-desc-input" /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setCreateOpen(false)} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={create} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="confirm-create-product">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="edit-product-modal">
          <DialogHeader><DialogTitle>Edit Product {editTarget?.code}</DialogTitle></DialogHeader>
          {editTarget && (
            <div className="space-y-4">
              <div className="space-y-2"><Label className="text-neutral-300">Name</Label><Input value={editTarget.name} onChange={(e) => setEditTarget((t) => ({ ...t, name: e.target.value }))} className={inputCls} data-testid="edit-product-name" /></div>
              <div className="space-y-2"><Label className="text-neutral-300">Description</Label><Textarea value={editTarget.description || ""} onChange={(e) => setEditTarget((t) => ({ ...t, description: e.target.value }))} rows={2} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="edit-product-desc" /></div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditTarget(null)} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={saveEdit} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="confirm-edit-product">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
